import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Grid, List, ListItem, TextField, makeStyles } from "@material-ui/core";
import { Autocomplete, Stack, Switch, Typography } from "@mui/material";
import { blue } from "@material-ui/core/colors";
import { List as ListIcon, CheckRounded } from "@material-ui/icons";
import RegularButton from "components/CustomButtons/Button";
import { LoadingComponent } from "components/Loading/LoadingComponent";
import CardTable from "components/CardTable/CardTable";
import { ModalV2 } from "components/Modal/ModalV2";
import { GlobalBox } from "components/Box/GlobalBox";
import { animations } from "helpers/animations";
import { useSearch } from "hooks/useSearch";
import { useToggle } from "hooks/useToggle";
import { noGN3, SAT } from "./columns";
import { DragAndDropBox } from "./DragAndDropBox";
import { useXlsxControls } from "./hook";
import styles from "./styles";
import { postFiles, resetState } from "redux/actions/gasMovDataXLSX/gasMovDataXLSXActions";

const useStyles = makeStyles(styles);

export const UploadFromXLSX = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { xlsx, xml } = useSelector(({ gasMovDataXLSX }) => gasMovDataXLSX);
  const { token } = useSelector(({ login }) => login);
  const { stationsList } = useSelector(({ companies }) => companies);
  const { isLoadingComp } = useSelector(({ search }) => search);
  const { processXLSX, processXML, loading } = useXlsxControls();
  const { buildCompLoadings } = useSearch();

  const [infoType, setInfoType] = useState(true);
  const [documentType, setDocumentType] = useState(true);
  const [stationSelected, setStationSelected] = useState("");
  const [open, setOpen] = useToggle();

  const handleStationChange = (e, v) => {
    const value = v ? v : "";
    setStationSelected(value);
  };

  const handleReset = () => dispatch(resetState());

  const handleSubmit = () => {
    let files = documentType ? xlsx : xml;
    files = files.map((item) => ({
      fileName: item.fileName,
      file: item.file,
      id: item.id,
    }));
    dispatch(postFiles(stationSelected?.id, files, token, setOpen, documentType ? "xlsx" : "xml", infoType ? "NoGN3" : "SAT"));
  };

  useEffect(() => buildCompLoadings(["loadingXlsx"]), []);

  return (
    <>
      <GlobalBox
        title={`Subir datos desde ${documentType ? "XLSX" : "XML"}`}
        icon={<ListIcon />}
        controls={
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} style={{ display: "flex", justifyContent: "flex-end" }}>
                <Stack
                  className={animations.fadeIn}
                  direction="row"
                  component="label"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Typography sx={{ color: !documentType ? blue[900] : "" }}>XML</Typography>
                  <Switch
                    style={{ alignSelf: "center" }}
                    checked={documentType}
                    color="primary"
                    onChange={() => setDocumentType(!documentType)}
                    className={classes.customSwitch}
                  />
                  <Typography sx={{ color: documentType ? blue[900] : "" }}>XLSX</Typography>
                </Stack>
              </Grid>
              {documentType &&
                <Grid item xs={12} style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Stack
                    className={animations.fadeIn}
                    direction="row"
                    component="label"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Typography sx={{ color: !infoType ? blue[900] : "" }}>SAT</Typography>
                    <Switch
                      style={{ alignSelf: "center" }}
                      checked={infoType}
                      color="primary"
                      onChange={() => setInfoType(!infoType)}
                      className={classes.customSwitch}
                    />
                    <Typography sx={{ color: infoType ? blue[900] : "" }}>NoGN3</Typography>
                  </Stack>
                </Grid>
              }
            </Grid>
          </>
        }
      >
        {documentType ?
          <CardTable
            columns={infoType ? noGN3 : SAT}
            isLoading={isLoadingComp?.loadingXlsx}
            data={xlsx.map(file => file.rows).flat()}
            tableContent={
              <>
                {loading
                  ? <LoadingComponent label="Procesando archivo..." height="20vh" />
                  : <DragAndDropBox fileProcessor={processXLSX} documentType={documentType} infoType={infoType} />
                }
                {xlsx.length > 0 &&
                  <div style={{ display: "flex", justifyContent: "center", width: "100%", padding: 20 }}>
                    <RegularButton color="linkedin" onClick={handleReset} style={{ marginRight: 8 }}>
                      Nuevo
                    </RegularButton>
                    <RegularButton color="success" onClick={setOpen}>
                      Guardar Registros
                    </RegularButton>
                  </div>
                }
              </>
            }
          />
          : <Card style={{ padding: 20 }} className={animations.fadeIn}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6} className={animations.fadeIn}>
                <Autocomplete
                  options={stationsList}
                  getOptionLabel={(option) => option.label ? option.label : option.name}
                  renderInput={(params) => (
                    <TextField {...params} label="Planta" variant="outlined" />
                  )}
                  fullWidth
                  onChange={(e, v) => handleStationChange(e, v)}
                  noOptionsText="No se encuentra la planta"
                />
              </Grid>
              {xml.length > 0 && stationSelected?.id &&
                <Grid item xs={10} md={6} className={animations.fadeIn}>
                  <RegularButton color="linkedin" onClick={handleReset} style={{ marginRight: 8 }}>
                    Nuevo
                  </RegularButton>
                  <RegularButton color="success" onClick={setOpen}>
                    Guardar Registros
                  </RegularButton>
                </Grid>
              }
              {xml.length > 0 &&
                <Grid item xs={12} style={{ display: "flex" }}>
                  <List style={{ maxHeight: 300, overflow: "hidden", overflowY: "scroll" }}>
                    {xml.map(file => (
                      <ListItem key={file.id} style={{ justifyContent: "space-between" }}>
                        <Typography style={{ alignSelf: "center" }}>file:&nbsp;{file.fileName}&nbsp;&nbsp;</Typography>
                        {file.status === "OK"
                          ? <CheckRounded style={{ alignSelf: "center", color: "green" }} />
                          : file.status.includes("ERROR")
                            ? <Typography style={{ alignSelf: "center", color: "red" }}>{file.status}</Typography>
                            : null
                        }
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              }
              <Grid item xs={12} className={animations.fadeIn}>
                <DragAndDropBox fileProcessor={processXML} documentType={documentType} />
              </Grid>
            </Grid>
          </Card>
        }
      </GlobalBox>
      <ModalV2
        open={open}
        onClose={setOpen}
        title="¿Estás seguro que deseas guardar los registros de estos archivos?"
        minHeight={0.1}
      >
        <List style={{ maxHeight: 300, overflow: "hidden", overflowY: "scroll" }}>
          {(documentType ? xlsx : xml).map((fl) => (
            <ListItem key={fl.id}>
              {fl.fileName}
            </ListItem>
          ))}
        </List>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, marginTop: 32 }}>
          <RegularButton onClick={setOpen} color="danger">
            Cancelar
          </RegularButton>
          <RegularButton onClick={handleSubmit} color="info">
            Aceptar
          </RegularButton>
        </div>
      </ModalV2>
    </>
  );
};
